import { useState, useEffect, useRef } from 'preact/hooks';
import { t } from '../i18n';
import type { WidgetConfig } from '../config';
import { emit } from '../analytics';
import { resolveAuthViewFromUrl } from '../navigation';
import { openContextualLogin, openContextualSignup } from '../returnTo';
import { formatPrice } from '../utils/currency';
import { store } from '../store';
import { cancelAuthCartRefresh, invalidateAuthAndCartGetCache } from '../api';
import { DEFAULT_AUTH_DISPLAY_SETTINGS, formatAuthDisplayLabel, type AuthDisplaySettings } from '../authDisplay';

type AuthUser = {
  login?: string;
  name?: string;
  customer_login?: string;
  customer_name?: string;
  firstname?: string;
  lastname?: string;
  customer_firstname?: string;
  customer_lastname?: string;
  suc_count?: number;
  bonus_account?: number;
} | null;

export type AccountViewType = import('../store').AccountView;

interface AuthWidgetProps {
  config: WidgetConfig;
  auth: AuthUser;
  onAuthChange: () => void | Promise<void>;
  onOpenAccountView?: (view: NonNullable<AccountViewType>) => void;
  /** Hide customer name next to the account icon (mobile header compact). */
  hideLoginLabel?: boolean;
  displaySettings?: AuthDisplaySettings;
}

function hasUser(auth: AuthUser): boolean {
  return !!(auth?.login ?? auth?.customer_login);
}

function resolveAuthNavHref(url: string | undefined, hashFallback: '#signup' | '#login'): string {
  const trimmed = typeof url === 'string' ? url.trim() : '';
  if (trimmed.startsWith('http') || trimmed.startsWith('/')) return trimmed;
  return hashFallback;
}

function resolveCurrentAccountView(): AccountViewType {
  const storeView = store.getAccountView();
  if (storeView) return storeView;
  if (typeof window === 'undefined') return null;
  return resolveAuthViewFromUrl(window.location.href);
}

export function AuthWidget({
  config,
  auth,
  onAuthChange,
  onOpenAccountView,
  hideLoginLabel = false,
  displaySettings = DEFAULT_AUTH_DISPLAY_SETTINGS,
}: AuthWidgetProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const personalDiscountEnabled = config.personalDiscountEnabled !== false;
  const bonusAccountEnabled = config.bonusAccountEnabled !== false;
  const referralProgramEnabled = config.referralProgramEnabled !== false;
  const hasTopSummaryItems = personalDiscountEnabled || bonusAccountEnabled;
  const activeView = resolveCurrentAccountView();
  const itemClass = (view: NonNullable<AccountViewType>, extra = '') =>
    `dropdown-item${extra ? ` ${extra}` : ''}${activeView === view ? ' active' : ''}`;
  const ariaCurrent = (view: NonNullable<AccountViewType>) => activeView === view ? 'page' : undefined;
  const loginLabel = formatAuthDisplayLabel(auth, displaySettings.customerNameMode);
  const fullLoginLabel = formatAuthDisplayLabel(auth, 'full');
  const displayClasses = [
    `sl-auth-display--${displaySettings.guestLinksMode.replaceAll('_', '-')}`,
    displaySettings.mobileIconOnly ? 'sl-auth-display--mobile-icon-only' : '',
  ].filter(Boolean).join(' ');

  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      const target = event.target as Node | null;
      if (dropdownRef.current && target && !dropdownRef.current.contains(target)) {
        setDropdownOpen(false);
      }
    };
    // Use click (not pointerdown): pointerdown-outside closes the menu before
    // the item click fires when header overflow steals hit-testing.
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!dropdownOpen || !dropdownRef.current || typeof window === 'undefined') return;
    const menu = dropdownRef.current.querySelector('.sl-auth-menu') as HTMLElement | null;
    const toggle = dropdownRef.current.querySelector('.sl-auth-toggle') as HTMLElement | null;
    if (!menu) return;

    const clearFixed = () => {
      menu.style.removeProperty('position');
      menu.style.removeProperty('top');
      menu.style.removeProperty('right');
      menu.style.removeProperty('left');
      menu.style.removeProperty('z-index');
      menu.style.removeProperty('pointer-events');
      menu.style.removeProperty('max-width');
      menu.style.removeProperty('min-width');
    };

    const placeHeaderMenu = () => {
      // Accordion inside drawer/nav must stay in-flow.
      if (menu.closest('.sl-nav, .sl-header-drawer, .sl-mobile-menu-only')) {
        clearFixed();
        return;
      }
      const inHeader = !!dropdownRef.current?.closest('.sl-header');
      if (!inHeader || !toggle) {
        clearFixed();
        return;
      }
      const r = toggle.getBoundingClientRect();
      const width = Math.min(288, Math.max(200, window.innerWidth - 24));
      const right = Math.max(12, window.innerWidth - r.right);
      menu.style.setProperty('position', 'fixed', 'important');
      menu.style.setProperty('top', `${Math.round(r.bottom + 8)}px`, 'important');
      menu.style.setProperty('right', `${Math.round(right)}px`, 'important');
      menu.style.setProperty('left', 'auto', 'important');
      menu.style.setProperty('z-index', '6000', 'important');
      menu.style.setProperty('pointer-events', 'auto', 'important');
      menu.style.setProperty('min-width', `${width}px`, 'important');
      menu.style.setProperty('max-width', `min(320px, calc(100vw - 1.5rem))`, 'important');
    };

    const raf = window.requestAnimationFrame(() => {
      placeHeaderMenu();
      const navScroller = menu.closest('.sl-nav') as HTMLElement | null;
      if (navScroller) {
        const navRect = navScroller.getBoundingClientRect();
        const menuRect = menu.getBoundingClientRect();
        const pad = 12;
        const bottomOverflow = menuRect.bottom - navRect.bottom + pad;
        if (bottomOverflow > 0) navScroller.scrollTop += bottomOverflow;
      }
    });
    const onReposition = () => placeHeaderMenu();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
      clearFixed();
    };
  }, [dropdownOpen]);

  const openView = (view: NonNullable<AccountViewType>) => (e: Event) => {
    e.preventDefault();
    e.stopPropagation();
    setDropdownOpen(false);
    onOpenAccountView?.(view);
  };

  if (hasUser(auth)) {
    return (
      <div class={`dropdown sl-auth-dropdown ${displayClasses}${hideLoginLabel ? ' sl-auth-dropdown--icon' : ''}`} ref={dropdownRef}>
        <button
          type="button"
          class="nav-link sl-auth-toggle dropdown-toggle d-flex align-items-center gap-1"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setDropdownOpen((v) => !v);
          }}
          aria-expanded={dropdownOpen}
          aria-haspopup="menu"
          aria-label={fullLoginLabel || t('auth.myAccount')}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            style={{ flex: '0 0 auto', opacity: 0.95 }}
          >
            <circle cx="12" cy="8" r="4" stroke="currentColor" stroke-width="1.8" />
            <path d="M4 20c0-3.6 3.6-6 8-6s8 2.4 8 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
          </svg>
          {!hideLoginLabel && loginLabel ? <span class="sl-auth-login-label">{loginLabel}</span> : null}
        </button>
        <ul
          class={`dropdown-menu sl-auth-menu ${dropdownOpen ? 'show' : ''}`}
          onClick={(e) => e.stopPropagation()}
        >
          {personalDiscountEnabled && (
            <li>
              <a class={itemClass('discounts', 'link-success')} aria-current={ariaCurrent('discounts')} href="#" onClick={openView('discounts')}>
                <strong>{auth?.suc_count ?? 0}%</strong> {t('auth.discount')}
              </a>
            </li>
          )}
          {bonusAccountEnabled && (
            <li>
              <a class={itemClass('bonus-transactions', 'link-success')} aria-current={ariaCurrent('bonus-transactions')} href="#" onClick={openView('bonus-transactions')}>
                <strong>{formatPrice(Math.max(0, Number(auth?.bonus_account) || 0))}</strong> {t('auth.bonusAccount')}
              </a>
            </li>
          )}
          {hasTopSummaryItems && <li><hr class="dropdown-divider" /></li>}
          <li><a class={itemClass('account')} aria-current={ariaCurrent('account')} href="#" onClick={openView('account')}>{t('auth.myAccount')}</a></li>
          {bonusAccountEnabled && (
            <li>
              <a class={itemClass('bonus-account')} aria-current={ariaCurrent('bonus-account')} href="#" onClick={openView('bonus-account')}>
                <span style="display:inline-flex;align-items:center;gap:.35rem">
                  {t('auth.bonusAccount')}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ color: '#f59e0b' }}>
                    <path d="M10 21H6a2 2 0 0 1-2-2v-6a2 2 0 0 1 2-2h4m0 10h6.2c1.3 0 2.4-1 2.6-2.3l.8-5A2.2 2.2 0 0 0 17.4 11H14V7.5c0-1.4-.8-2.5-2-3l-2 5.5V21z" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round" />
                  </svg>
                </span>
              </a>
            </li>
          )}
          {referralProgramEnabled && (
            <li><a class={itemClass('referral-tree')} aria-current={ariaCurrent('referral-tree')} href="#" onClick={openView('referral-tree')}>{t('auth.referral')}</a></li>
          )}
          <li><a class={itemClass('orders')} aria-current={ariaCurrent('orders')} href="#" onClick={openView('orders')}>{t('auth.orders')}</a></li>
          <li><a class={itemClass('subscriptions')} aria-current={ariaCurrent('subscriptions')} href="#" onClick={openView('subscriptions')}>{t('auth.subscriptions')}</a></li>
          <li><a class={itemClass('tickets')} aria-current={ariaCurrent('tickets')} href="#" onClick={openView('tickets')}>{t('auth.tickets')}</a></li>
          <li><a class={itemClass('account-delete')} aria-current={ariaCurrent('account-delete')} href="#" onClick={openView('account-delete')}>{t('auth.deleteAccount')}</a></li>
          <li><hr class="dropdown-divider" /></li>
          <li>
            <button
              type="button"
              class="dropdown-item link-success border-0 bg-transparent w-100 text-start"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDropdownOpen(false);
                void logout(config, onAuthChange);
              }}
            >
              {t('auth.logout')}
            </button>
          </li>
        </ul>
      </div>
    );
  }

  const signupUrl = config.viewUrls?.signup;
  const loginUrl = config.viewUrls?.['checkout-login'];
  const signupHref = resolveAuthNavHref(signupUrl, '#signup');
  const loginHref = resolveAuthNavHref(loginUrl, '#login');

  return (
    <div class={`d-flex align-items-center sl-auth-guest ${displayClasses}`}>
      <a
        href={signupHref}
        data-sw-view="signup"
        class="nav-link me-1 text-uppercase fw-bold sl-auth-signup-link"
        style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
        onClick={(e) => { e.preventDefault(); openContextualSignup(); }}
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ flex: '0 0 auto' }}>
          <circle cx="10" cy="8" r="3.5" stroke="currentColor" stroke-width="1.8" />
          <path d="M3.5 19c0-3.2 3.2-5.3 6.5-5.3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
          <path d="M17.5 9.5v7m-3.5-3.5h7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
        </svg>
        <span class="sl-auth-signup-label">{t('auth.signup')}</span>
      </a>
      <a
        href={loginHref}
        data-sw-view="checkout-login"
        class="nav-link text-uppercase fw-bold sl-auth-login-link"
        aria-label={t('auth.login')}
        style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
        onClick={(e) => { e.preventDefault(); openContextualLogin(); }}
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ flex: '0 0 auto' }}>
          <path d="M14 4h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
          <path d="M10 16l4-4-4-4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
          <path d="M14 12H4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
        </svg>
        <span class="sl-auth-login-label">{t('auth.login')}</span>
      </a>
    </div>
  );
}

function resolvePreviewProxyHomeUrl(config: WidgetConfig): string | null {
  if (typeof window === 'undefined') return null;
  const cur = new URL(window.location.href);
  const inProxy = cur.pathname.includes('/preview-proxy');
  const homeRaw = config.viewUrls?.home?.trim() || '';
  let target: URL | null = null;
  if (homeRaw) {
    try {
      target = new URL(homeRaw, cur.origin);
    } catch {
      target = null;
    }
  }
  if (!inProxy && !(target && target.pathname.includes('/preview-proxy'))) {
    return null;
  }
  if (!target || !target.pathname.includes('/preview-proxy')) {
    target = new URL(cur.href);
  }
  target.searchParams.set('path', '/');
  for (const key of ['preview_token', 'access_token', 'draft', 'lang', 'preview_gateway'] as const) {
    const v = cur.searchParams.get(key);
    if (v && !target.searchParams.get(key)) target.searchParams.set(key, v);
  }
  for (const key of ['category', 'subcategory', 'symptom', 'sort', 'page', 'key', 'product_name'] as const) {
    target.searchParams.delete(key);
  }
  target.searchParams.set('_logout', String(Date.now()));
  return target.toString();
}

async function logout(config: WidgetConfig, onAuthChange: () => void | Promise<void>) {
  // Preview-proxy: leave ?path=/my-account/… immediately (hard nav keeps tokens).
  const proxyHome = resolvePreviewProxyHomeUrl(config);
  if (proxyHome) {
    try {
      cancelAuthCartRefresh();
      invalidateAuthAndCartGetCache(config);
    } catch {
      // ignore
    }
    store.setAuth(null);
    store.setCart(0, 0, []);
    store.setAccountView(null);
    emit('sw:logout');
    try {
      const base = (config.apiUrl || '').replace(/\/$/, '');
      if (base) {
        const url = new URL(`${base}/api/w/logout`, window.location.origin);
        if (config.siteKey) url.searchParams.set('site_key', config.siteKey);
        if (config.previewToken) url.searchParams.set('preview', config.previewToken);
        url.searchParams.set('_ts', String(Date.now()));
        void fetch(url.toString(), {
          method: 'POST',
          credentials: 'include',
          cache: 'no-store',
          keepalive: true,
        }).catch(() => null);
      }
    } catch {
      // ignore
    }
    window.location.assign(proxyHome);
    return;
  }

  // Live: one calm transition — await logout, then single home nav (no _logout flash).
  try {
    const base = (config.apiUrl || '').replace(/\/$/, '');
    if (base) {
      const url = new URL(`${base}/api/w/logout`, typeof window !== 'undefined' ? window.location.origin : 'https://localhost');
      if (config.siteKey) url.searchParams.set('site_key', config.siteKey);
      if (config.previewToken) url.searchParams.set('preview', config.previewToken);
      url.searchParams.set('_ts', String(Date.now()));
      await fetch(url.toString(), {
        method: 'POST',
        credentials: 'include',
        cache: 'no-store',
      });
    }
  } catch {
    // ignore network errors — still clear local session
  }

  try {
    cancelAuthCartRefresh();
    invalidateAuthAndCartGetCache(config);
  } catch {
    // ignore
  }
  store.setAuth(null);
  store.setCart(0, 0, []);
  store.setAccountView(null);
  emit('sw:logout');
  await Promise.resolve(onAuthChange());

  if (typeof window === 'undefined') return;
  const homeUrl = config.viewUrls?.home || '/';
  try {
    const target = new URL(homeUrl, window.location.origin);
    const samePath = window.location.pathname === target.pathname;
    if (samePath) {
      // Already on home — soft UI update only, no reload flicker.
      return;
    }
    window.location.href = target.pathname + target.search + target.hash;
  } catch {
    window.location.href = homeUrl;
  }
}
